import * as React from "react";
import { Input } from "./input";
import { sanitizeSearchInput } from "../../utils/search";

type SearchInputProps = Omit<React.ComponentProps<"input">, "onChange" | "value" | "type"> & {
  value: string;
  onValueChange: (value: string) => void;
};

/**
 * Industry-standard search field: typing, paste, and IME all update the same state.
 * Paste is applied from clipboard so Ctrl+V / right-click paste cannot be missed.
 */
export function SearchInput({ value, onValueChange, className, onPaste, ...props }: SearchInputProps) {
  const apply = React.useCallback(
    (next: string) => {
      onValueChange(sanitizeSearchInput(next));
    },
    [onValueChange]
  );

  return (
    <Input
      type="search"
      enterKeyHint="search"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      value={value}
      onChange={(e) => apply(e.target.value)}
      onInput={(e) => apply((e.target as HTMLInputElement).value)}
      onPaste={(e) => {
        onPaste?.(e);
        if (e.defaultPrevented) return;
        const pasted =
          e.clipboardData?.getData("text/plain") || e.clipboardData?.getData("text") || "";
        const start = e.currentTarget.selectionStart ?? value.length;
        const end = e.currentTarget.selectionEnd ?? value.length;
        const next = value.slice(0, start) + pasted + value.slice(end);
        e.preventDefault();
        apply(next);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          apply(e.currentTarget.value);
        }
      }}
      className={className}
      {...props}
    />
  );
}
